import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PencilSimple, Trash, CheckCircle } from "@phosphor-icons/react";
import { Header, F, IconBtn, Modal } from "@/components/Clients";

const empty = {
  title: "",
  tax_type: "GST",
  form_code: "",
  due_date: "",
  client_id: "",
  status: "pending",
  notes: "",
};

const Deadlines = ({ readOnly = false }) => {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/deadlines").then((r) => setRows(r.data.deadlines));

  useEffect(() => {
    load();
    if (!readOnly) api.get("/clients").then((r) => setClients(r.data.clients));
  }, [readOnly]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (d) => { setEditing(d); setForm({ ...empty, ...d, client_id: d.client_id || "" }); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, client_id: form.client_id || null };
      if (editing) await api.put(`/deadlines/${editing.id}`, body);
      else await api.post("/deadlines", body);
      toast.success("Saved");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.title}"?`)) return;
    await api.delete(`/deadlines/${d.id}`);
    load();
  };

  const markDone = async (d) => {
    await api.put(`/deadlines/${d.id}`, { ...d, status: "completed", client_id: d.client_id || null });
    load();
  };

  return (
    <div>
      <Header
        title="Tax Deadlines"
        subtitle="GST · TDS · ITR · Professional Tax"
        onNew={readOnly ? undefined : openNew}
        newLabel="New Deadline"
        testId="deadline"
      />

      <div className="mt-6 border border-[#E8EAE8] bg-white">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[#E8EAE8] text-[11px] uppercase tracking-widest text-[#5C6B64] font-semibold">
          <div className="col-span-1">Type</div>
          <div className="col-span-4">Title</div>
          <div className="col-span-2">Form</div>
          <div className="col-span-2">Client</div>
          <div className="col-span-1 font-mono-data">Due</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Actions</div>
        </div>
        {rows.length === 0 && <div className="p-6 text-sm text-[#5C6B64]">No deadlines yet.</div>}
        {rows.map((d) => (
          <div key={d.id} data-testid={`deadline-row-${d.id}`} className="grid grid-cols-12 gap-4 px-5 py-4 border-b last:border-b-0 border-[#E8EAE8] items-center">
            <div className="col-span-1">
              <span className={`inline-block text-[10px] font-semibold uppercase tracking-widest px-2 py-1 ${typeCls(d.tax_type)}`}>{d.tax_type}</span>
            </div>
            <div className="col-span-4 text-[#0F2E22]">{d.title}</div>
            <div className="col-span-2 font-mono-data text-xs">{d.form_code || "—"}</div>
            <div className="col-span-2 text-sm">{d.client_name || "—"}</div>
            <div className="col-span-1 font-mono-data text-sm">{d.due_date}</div>
            <div className="col-span-1">
              <span className={`text-[10px] uppercase tracking-widest ${d.status === "completed" ? "text-[#2D7A4D]" : "text-[#A54A00]"}`}>{d.status}</span>
            </div>
            <div className="col-span-1 flex justify-end gap-2">
              {!readOnly && d.status !== "completed" && (
                <IconBtn onClick={() => markDone(d)} testId={`complete-${d.id}`}><CheckCircle size={14} /></IconBtn>
              )}
              {!readOnly && <IconBtn onClick={() => openEdit(d)} testId={`edit-deadline-${d.id}`}><PencilSimple size={14} /></IconBtn>}
              {!readOnly && <IconBtn onClick={() => remove(d)} testId={`delete-deadline-${d.id}`}><Trash size={14} /></IconBtn>}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <Modal title={editing ? "Edit Deadline" : "New Deadline"} onClose={() => setShowForm(false)}>
          <form onSubmit={save} className="space-y-3">
            <F label="Title" value={form.title} onChange={set("title")} required testId="form-title" />
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-[#5C6B64]">Tax type</span>
                <select data-testid="form-tax-type" value={form.tax_type} onChange={set("tax_type")} className="mt-1 w-full border border-[#E8EAE8] px-3 py-2 text-sm bg-white">
                  {["GST", "TDS", "ITR", "PT", "Other"].map((x) => <option key={x}>{x}</option>)}
                </select>
              </label>
              <F label="Form code" value={form.form_code} onChange={set("form_code")} mono testId="form-code" placeholder="e.g. GSTR-3B" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F label="Due date" type="date" value={form.due_date} onChange={set("due_date")} required testId="form-due" />
              <label className="block">
                <span className="text-xs uppercase tracking-widest text-[#5C6B64]">Client</span>
                <select data-testid="form-client" value={form.client_id} onChange={set("client_id")} className="mt-1 w-full border border-[#E8EAE8] px-3 py-2 text-sm bg-white">
                  <option value="">— None —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </label>
            </div>
            <F label="Notes" value={form.notes} onChange={set("notes")} testarea testId="form-notes" />
            <button type="submit" data-testid="save-deadline-btn" className="w-full py-3 bg-[#0F2E22] text-white text-sm tracking-widest uppercase hover:opacity-90">Save</button>
          </form>
        </Modal>
      )}
    </div>
  );
};

const typeCls = (t) => {
  switch (t) {
    case "GST": return "bg-[#DDE9FF] text-[#1A44A8]";
    case "TDS": return "bg-[#FFEBD6] text-[#A54A00]";
    case "ITR": return "bg-[#FFD6D6] text-[#A31A1A]";
    case "PT": return "bg-[#E5F5E9] text-[#2D7A4D]";
    default: return "bg-[#EEE] text-[#333]";
  }
};

export default Deadlines;
